import React, { useEffect, useState, useCallback } from 'react';
import { X, Star, MapPin, Lock, Loader2, Check } from 'lucide-react';
import { centerToast } from './CenterToast';
import {
  IHSAN_POINTS,
  POINT_VALUE_SGD,
  formatPoints,
  REDEMPTION_PLACES,
  REDEMPTION_REWARDS,
  listRewards,
  redeemReward,
  redemptionErrorMessage
} from '../lib/rewards';

/**
 * RewardsSheet — what opens behind the points pill in the top bar.
 *
 * Backed by the real 007 rewards migration now. Catalogue comes from
 * `listRewards()`, redemption goes through `redeemReward()` (SECURITY DEFINER
 * RPC, atomic + idempotent). The static REDEMPTION_REWARDS from lib/rewards
 * is a fallback for the first-paint / cold-schema case only.
 *
 * `onBalanceChange(newBalance)` bubbles the debited balance up so the top bar
 * and Me tab update immediately without waiting for a profile refetch.
 */
export default function RewardsSheet({ isOpen, onClose, points = IHSAN_POINTS, onBalanceChange }) {
  const [catalogue, setCatalogue] = useState(REDEMPTION_REWARDS);
  const [catalogueLoaded, setCatalogueLoaded] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [redeemed, setRedeemed] = useState({}); // reward_id -> { redemption_id, at }

  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  // Load the live catalogue when the sheet opens (cache across opens).
  useEffect(() => {
    if (!isOpen || catalogueLoaded) return;
    let live = true;
    (async () => {
      try {
        const rows = await listRewards();
        if (live && Array.isArray(rows) && rows.length) {
          setCatalogue(rows);
        }
      } catch (err) {
        // Fall back to the static list — the sheet still works.
        // eslint-disable-next-line no-console
        console.info('[rewards] catalogue fetch failed, using fallback', err);
      } finally {
        if (live) setCatalogueLoaded(true);
      }
    })();
    return () => { live = false; };
  }, [isOpen, catalogueLoaded]);

  const dollarValue = (points * POINT_VALUE_SGD).toFixed(2);

  const handleRedeem = useCallback(async (reward) => {
    if (busyId) return;
    if (points < reward.cost) {
      centerToast(`You need ${formatPoints(reward.cost - points)} more pts`, '🔒');
      return;
    }
    setBusyId(reward.id);
    try {
      const result = await redeemReward(reward.id);
      setRedeemed((prev) => ({
        ...prev,
        [reward.id]: {
          redemption_id: result.redemption_id,
          at: Date.now(),
          replayed: !!result.idempotent_replay
        }
      }));
      if (typeof onBalanceChange === 'function' && typeof result.balance_after === 'number') {
        onBalanceChange(result.balance_after);
      }
      centerToast(
        result.idempotent_replay
          ? `Already redeemed: ${reward.title}`
          : `Redeemed: ${reward.title}`,
        '🎁'
      );
    } catch (err) {
      const code = err?.code || 'redemption_failed';
      centerToast(
        redemptionErrorMessage(code, { needed: reward.cost - points }),
        code === 'insufficient_points' ? '🔒' : '⚠️'
      );
    } finally {
      setBusyId(null);
    }
  }, [busyId, points, onBalanceChange]);

  return (
    <>
      <div
        aria-hidden="true"
        onClick={onClose}
        className={`fixed inset-0 z-40 bg-black/40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      />

      <div
        data-testid="rewards-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Your Ihsan Points"
        className={`fixed inset-x-0 bottom-0 mx-auto max-w-[480px] z-50 bg-background rounded-t-3xl shadow-2xl max-h-[88vh] flex flex-col transition-transform duration-300 ease-out ${
          isOpen ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="pt-3 pb-1 flex justify-center flex-shrink-0">
          <span className="w-10 h-1.5 rounded-full bg-muted-foreground/30" />
        </div>

        <div className="px-5 pb-4 flex items-start justify-between gap-3 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold leading-tight">Your Ihsan Points</h2>
            <p className="text-sm text-muted-foreground mt-1">Earned by learning, serving and giving.</p>
          </div>
          <button
            type="button"
            data-testid="rewards-sheet-close"
            onClick={onClose}
            aria-label="Close"
            className="flex-shrink-0 w-9 h-9 rounded-full bg-muted hover:bg-muted/70 flex items-center justify-center"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 pb-6 space-y-6">
          <div className="rounded-2xl border-2 border-[#D4A843]/40 bg-amber-50 p-5 flex items-center gap-4">
            <span className="h-12 w-12 rounded-full bg-white flex items-center justify-center flex-shrink-0">
              <Star className="h-6 w-6 text-[#D4A843] fill-[#D4A843]" />
            </span>
            <div className="min-w-0">
              <p className="text-3xl font-extrabold text-[#0F172A] leading-none" data-testid="rewards-balance">
                {formatPoints(points)}
                <span className="text-base font-bold text-muted-foreground ml-1.5">pts</span>
              </p>
              <p className="text-xs text-muted-foreground mt-1.5">Worth about S${dollarValue} in credits</p>
            </div>
          </div>

          <section>
            <h3 className="text-base font-bold text-[#0F172A] mb-3">What you can get</h3>
            <div className="space-y-2">
              {catalogue.map((reward) => {
                const affordable = points >= reward.cost;
                const outOfStock = reward.stock !== undefined && reward.stock !== null && reward.stock <= 0;
                const isBusy = busyId === reward.id;
                const done = redeemed[reward.id];
                const disabled = isBusy || outOfStock || !affordable || !!done;
                return (
                  <button
                    key={reward.id}
                    type="button"
                    data-testid={`reward-${reward.id}`}
                    disabled={disabled}
                    onClick={() => handleRedeem(reward)}
                    className={`w-full flex items-center gap-3 rounded-xl border border-border bg-card px-3.5 py-3 text-left transition-colors ${
                      disabled ? 'opacity-60 cursor-not-allowed' : 'hover:bg-muted/50'
                    }`}
                  >
                    <span className="text-2xl leading-none flex-shrink-0" aria-hidden="true">{reward.emoji || '🎁'}</span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold text-[#0F172A] truncate">{reward.title}</span>
                      <span className="block text-xs text-muted-foreground mt-0.5">
                        {formatPoints(reward.cost)} pts
                        {outOfStock ? ' · out of stock' : ''}
                        {done ? ' · redeemed' : ''}
                      </span>
                    </span>
                    {isBusy && <Loader2 className="h-4 w-4 text-muted-foreground animate-spin flex-shrink-0" />}
                    {!isBusy && done && <Check className="h-4 w-4 text-primary flex-shrink-0" data-testid={`reward-${reward.id}-done`} />}
                    {!isBusy && !done && !affordable && <Lock className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          </section>

          <section>
            <h3 className="text-base font-bold text-[#0F172A] mb-3">Where you can redeem</h3>
            <div className="space-y-2">
              {REDEMPTION_PLACES.map((place) => (
                <div
                  key={place.id}
                  data-testid={`redeem-place-${place.id}`}
                  className="flex items-start gap-3 rounded-xl border border-border bg-card px-3.5 py-3"
                >
                  <MapPin className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#0F172A]">{place.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{place.detail}</p>
                    <p className="text-xs text-primary font-medium mt-1">{place.note}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <p className="text-xs text-muted-foreground leading-relaxed border-t border-border pt-4">
            Redemptions are recorded to your Ihsan Points ledger and marked pending fulfilment. Show your redemption at the counter or wait for MTFA to apply the credit.
          </p>
        </div>
      </div>
    </>
  );
}
